import { useOrgIntegrations } from "@/api/integrations";
import { useSkills } from "@/api/skills";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  MAX_SKILL_DESCRIPTION_LENGTH,
  skillDescriptionLength,
} from "@/utils/skillDescription";
import { Skill } from "@cohesive/api-client";
import { Icon, IconSelector } from "@cohesive/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Plus } from "lucide-react";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const iconSchema = z
  .object({
    type: z.enum(["emoji", "lucide", "custom"]),
    emoji: z.string().optional(),
    lucide: z
      .object({
        name: z.string(),
        color: z.string(),
      })
      .optional(),
    custom: z
      .object({
        url: z.string(),
      })
      .optional(),
  })
  .or(z.any());

const SKILL_NAME_REGEX = /^[a-z0-9-]{2,48}$/;
const CONSECUTIVE_DASHES_RE = /---/;

function createSkillFormSchema(mode: "create" | "edit") {
  const nameSchema =
    mode === "edit"
      ? z.string().min(1, "Name is required")
      : z
          .string()
          .min(2, "Name must be at least 2 characters")
          .max(48, "Name must be 48 characters or fewer")
          .regex(
            SKILL_NAME_REGEX,
            "Only lowercase letters, numbers, and dashes are allowed",
          )
          .refine(
            (val) => !CONSECUTIVE_DASHES_RE.test(val),
            "Name cannot contain three or more consecutive dashes",
          );

  return z.object({
    name: nameSchema,
    // Required: the description is the skill's trigger surface, and the
    // server rejects skills without one.
    description: z
      .string()
      .min(1, "Description is required")
      .refine(
        (val) => skillDescriptionLength(val) <= MAX_SKILL_DESCRIPTION_LENGTH,
        `Description must be ${MAX_SKILL_DESCRIPTION_LENGTH} characters or fewer`,
      )
      .refine(
        (val) => !CONSECUTIVE_DASHES_RE.test(val),
        "Description cannot contain three or more consecutive dashes",
      ),
    icon: iconSchema.optional(),
    instructions: z
      .string()
      .max(200_000, "Instructions must be 200,000 characters or fewer")
      .optional(),
  });
}

type FormData = z.infer<ReturnType<typeof createSkillFormSchema>>;

const isValidIcon = (icon: any): boolean => {
  if (!icon || typeof icon !== "object") return false;
  const validTypes = ["emoji", "lucide", "custom"];
  return validTypes.includes(icon.type);
};

interface SkillCreateFormProps {
  mode?: "create" | "edit";
  initialSkill?: Skill;
  error?: string;
  onSave: (data: FormData) => void | Promise<void>;
}

export const SkillCreateForm: React.FC<SkillCreateFormProps> = ({
  mode = "create",
  initialSkill,
  error,
  onSave,
}) => {
  const { data: integrations = [] } = useOrgIntegrations();
  const { data: existingSkills = [] } = useSkills({
    enabled: mode === "create",
  });

  const schema = useMemo(() => createSkillFormSchema(mode), [mode]);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: initialSkill?.name || "",
      description: initialSkill?.description || "",
      icon: isValidIcon(initialSkill?.icon) ? initialSkill?.icon : undefined,
      instructions: "",
    },
  });

  const icon = form.watch("icon");
  const watchedName = form.watch("name");

  const nameTakenByExisting =
    mode === "create" &&
    watchedName.length >= 2 &&
    !form.formState.errors.name &&
    existingSkills.some((s) => s.name === watchedName);

  const handleSubmit = async (data: FormData) => {
    const cleanedData = {
      ...data,
      icon: isValidIcon(data.icon) ? data.icon : undefined,
    };
    await onSave(cleanedData);
  };

  return (
    <div className="mt-2">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          {error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Icon + Name row */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <div className="flex items-center gap-2">
                  <div className="shrink-0">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent h-9 w-9"
                        >
                          {isValidIcon(icon) ? (
                            <Icon icon={icon} size={18} />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-[352px]">
                        <IconSelector
                          onIconChange={(icon) => {
                            form.setValue("icon", icon, { shouldDirty: true });
                          }}
                          selectedIcon={isValidIcon(icon) ? icon : undefined}
                          integrations={integrations}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <FormControl>
                    <Input
                      placeholder="my-awesome-skill"
                      {...field}
                      disabled={mode === "edit"}
                      onChange={(e) => {
                        const normalized = e.target.value
                          .toLowerCase()
                          .replace(/\s+/g, "-")
                          .replace(/[^a-z0-9-]/g, "")
                          .replace(/-{3,}/g, "--");
                        field.onChange(normalized);
                      }}
                      maxLength={48}
                    />
                  </FormControl>
                </div>
                <FormMessage />
                {nameTakenByExisting && !form.formState.errors.name && (
                  <p className="flex items-center gap-1.5 text-sm text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />A skill with
                    this name already exists in your workspace.
                  </p>
                )}
                <FormDescription>
                  {mode === "edit"
                    ? "Skill name cannot be changed after creation."
                    : "Lowercase letters, numbers, and dashes only. Spaces convert to dashes automatically."}
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Description{" "}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="A short summary of what this skill does"
                    className="resize-none min-h-[80px]"
                    maxLength={MAX_SKILL_DESCRIPTION_LENGTH}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The agent reads this to decide when to use the skill.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {mode === "create" && (
            <FormField
              control={form.control}
              name="instructions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Instructions{" "}
                    <span className="text-muted-foreground font-normal">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Markdown instructions for the agent (saved as SKILL.md body)"
                      className="min-h-[180px] font-mono text-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Detailed guidance in SKILL.md — you can edit this later in
                    the skill file editor.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting}
              disabled={
                (mode === "edit" && !form.formState.isDirty) ||
                nameTakenByExisting
              }
            >
              {mode === "edit" ? "Update Skill" : "Create Skill"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
